import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  BLACKOUT_GROUP_NEW,
  BLACKOUT_GROUP_OFF,
  BLACKOUT_GROUP_SCOPE_FILTERS,
  BlackoutGroupScopeFilter,
  IDisallowedTimeRangeGroup,
  IMarkingWindowLabel,
  isGroupSelected,
  markingWindowLabel,
  otherAttachedWindows,
  toggleGroupSelection,
} from '../types';

/**
 * DELIV-17370 — searchable non-working hour group picker for one scoring window.
 *
 * Same group list the Scoring Leader sees in System Settings (name, timezone,
 * rule count, windows in scope), narrowed to the groups selectable for this
 * window and filterable by name or attachment, instead of a bare dropdown.
 *
 * A window may enforce several groups at once, so groups are checkboxes: the
 * rules of every checked group apply, and the task is blocked when any of them
 * blocks it.
 */
@Component({
  selector: 'blackout-hours-group-picker',
  templateUrl: './blackout-hours-group-picker.component.html',
  styleUrls: ['./blackout-hours-group-picker.component.scss'],
})
export class BlackoutHoursGroupPickerComponent {

  @Input() groups: IDisallowedTimeRangeGroup[] = [];
  /** Groups checked for this window (pending apply). */
  @Input() selectedGroupIds: number[] = [];
  /** Group ids already attached to this window, highlighted as in use. */
  @Input() activeGroupIds: number[] = [];
  /** True while the "+ Create a new group" row is the active choice. */
  @Input() isCreatingGroup: boolean = false;
  @Input() markingWindowId: number;
  @Input() disabled: boolean = false;
  /** Offer the "+ Create a new group" row (hidden when the caller cannot create). */
  @Input() allowCreate: boolean = true;
  /** Group whose rules are open in the editor, so the row can show it. */
  @Input() editingGroupId: number = BLACKOUT_GROUP_OFF;

  /** Emits the full selected set whenever a group is checked or unchecked. */
  @Output() selectedGroupIdsChange = new EventEmitter<number[]>();
  /** Emits when the leader picks a checked group to edit its rules. */
  @Output() editGroup = new EventEmitter<number>();
  /** Emits when the "+ Create a new group" row is chosen. */
  @Output() createGroup = new EventEmitter<void>();
  /** Emits when "Off" clears the whole selection. */
  @Output() clearSelection = new EventEmitter<void>();

  readonly OFF = BLACKOUT_GROUP_OFF;
  readonly NEW_GROUP = BLACKOUT_GROUP_NEW;
  readonly SCOPE_FILTERS = BLACKOUT_GROUP_SCOPE_FILTERS;

  searchText: string = '';
  scopeFilter: BlackoutGroupScopeFilter = 'all';

  markingWindowLabel = markingWindowLabel;

  get filteredGroups(): IDisallowedTimeRangeGroup[] {
    const needle = (this.searchText || '').trim().toLowerCase();
    return (this.groups || []).filter((group) => {
      if (needle) {
        const haystack = `${group.name || ''} ${group.timezone || ''} ${group.academic_year || ''}`.toLowerCase();
        if (haystack.indexOf(needle) === -1) return false;
      }
      return this.matchesScopeFilter(group);
    });
  }

  private matchesScopeFilter(group: IDisallowedTimeRangeGroup): boolean {
    const attached = group.attached_window_ids || [];
    switch (this.scopeFilter) {
      case 'this-window':
        return attached.some((id) => Number(id) === Number(this.markingWindowId));
      case 'shared':
        return this.otherWindows(group).length > 0;
      case 'unused':
        return attached.length === 0;
      default:
        return true;
    }
  }

  /** Windows other than this one that would be affected by editing this group. */
  otherWindows(group: IDisallowedTimeRangeGroup): IMarkingWindowLabel[] {
    return otherAttachedWindows(group, this.markingWindowId);
  }

  otherWindowsSummary(group: IDisallowedTimeRangeGroup): string {
    return this.otherWindows(group).map((w) => markingWindowLabel(w)).join(', ');
  }

  ruleCount(group: IDisallowedTimeRangeGroup): number {
    return (group.ranges || []).length;
  }

  /** Already attached on the server (as opposed to merely checked). */
  isActive(group: IDisallowedTimeRangeGroup): boolean {
    return isGroupSelected(this.activeGroupIds, group.id);
  }

  isSelected(groupId: number): boolean {
    return isGroupSelected(this.selectedGroupIds, groupId);
  }

  isEditing(groupId: number): boolean {
    return Number(this.editingGroupId) === Number(groupId) && Number(groupId) > 0;
  }

  get selectedCount(): number {
    return (this.selectedGroupIds || []).length;
  }

  get isOffSelected(): boolean {
    return this.selectedCount === 0 && !this.isCreatingGroup;
  }

  toggleGroup(groupId: number) {
    if (this.disabled) return;
    this.selectedGroupIdsChange.emit(
      toggleGroupSelection(this.selectedGroupIds || [], Number(groupId)),
    );
  }

  /** Row click: check the group, and open its rules when it is already checked. */
  onGroupRowClick(groupId: number) {
    if (this.disabled) return;
    if (this.isSelected(groupId)) {
      this.editGroup.emit(Number(groupId));
      return;
    }
    this.toggleGroup(groupId);
  }

  onEditGroupClick(groupId: number, event: Event) {
    event.stopPropagation();
    if (this.disabled) return;
    this.editGroup.emit(Number(groupId));
  }

  selectOff() {
    if (this.disabled) return;
    this.clearSelection.emit();
  }

  selectNewGroup() {
    if (this.disabled) return;
    this.createGroup.emit();
  }

  clearFilters() {
    this.searchText = '';
    this.scopeFilter = 'all';
  }

  get isFiltered(): boolean {
    return !!(this.searchText || '').trim() || this.scopeFilter !== 'all';
  }

  /** Checked groups the current filters hide, so the count still adds up. */
  get hiddenSelectedGroups(): IDisallowedTimeRangeGroup[] {
    const visible = this.filteredGroups;
    return (this.groups || []).filter((group) =>
      this.isSelected(group.id) && !visible.some((g) => Number(g.id) === Number(group.id)),
    );
  }

  get hiddenSelectedSummary(): string {
    return this.hiddenSelectedGroups.map((group) => group.name).join(', ');
  }

  trackById(index: number, group: IDisallowedTimeRangeGroup): number {
    return group.id;
  }
}
